import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { format, differenceInDays, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';

import {
  formatCurrency,
  formatCurrencyDecimals,
  formatNumber,
} from 'utils/formats';

import { TradingOfferOperation } from 'types/TradingOfferOperation';
import { TradingOffer } from 'types/TradingOffer';

const getDaysRelativeToToday = (date: Date | string | undefined) => {
  if (!date) return null;
  const target = startOfDay(new Date(date));
  const today = startOfDay(new Date());
  return differenceInDays(target, today);
};

const formatDate = (
  date: Date | string | undefined,
  pattern = 'dd MMM yyyy'
) => {
  if (!date) return '-';
  return format(new Date(date), pattern, { locale: es });
};

type TradingOfferOperationDetailSummaryCardProps = {
  operation: TradingOfferOperation | null;
  tradingOffer: TradingOffer | null;
  operationReference: string;
  offerReference: string;
  shareClassName?: string;
  formattedRequestDate: string;
  onGoToOffer: () => void;
};

const TradingOfferOperationDetailSummaryCard: FC<
  TradingOfferOperationDetailSummaryCardProps
> = ({
  operation,
  tradingOffer,
  operationReference,
  offerReference,
  shareClassName,
  formattedRequestDate,
  onGoToOffer,
}) => {
  const { t } = useTranslate();

  const completionTimeLabel = useMemo(() => {
    if (operation?.completionDate) {
      const days = getDaysRelativeToToday(operation.completionDate);
      if (days === null) return '';
      if (days === 0) return t('Today');
      return days > 0
        ? t('DaysRemainingCount', { count: days })
        : t('DaysAgoCount', { count: Math.abs(days) });
    }
    if (tradingOffer?.endDate) {
      const days = getDaysRelativeToToday(tradingOffer.endDate);
      if (days === null || days < 0) return '';
      if (days === 0) return t('Today');
      return t('DaysRemainingCount', { count: days });
    }
    return '';
  }, [operation?.completionDate, tradingOffer?.endDate, t]);

  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="card-title mb-0">
            <em className="icon ni ni-file-text text-primary mr-2" />
            {t('OperationSummary')}
          </h6>
          <span className="text-soft fs-12px">{operationReference}</span>
        </div>
        <div className="row g-4">
          <div className="col-6 col-md-4">
            <div
              className="h-100"
              style={{
                borderLeft: '3px solid #09c2de',
                paddingLeft: '12px',
              }}
            >
              <span className="overline-title text-soft">
                {t('OperationType')}
              </span>
              <h5 className="mb-0 mt-1">
                {tradingOffer?.type === 'SELL' ? t('Purchase') : t('Sale')}
              </h5>
              <span className="text-soft fs-13px">
                <button
                  type="button"
                  className="btn btn-link p-0 fs-13px"
                  onClick={onGoToOffer}
                >
                  {offerReference}
                </button>
              </span>
            </div>
          </div>
          <div className="col-6 col-md-4">
            <div
              className="h-100"
              style={{
                borderLeft: '3px solid #1ee0ac',
                paddingLeft: '12px',
              }}
            >
              <span className="overline-title text-soft">{t('Shares')}</span>
              <h5 className="mb-0 mt-1">
                {formatNumber(operation?.sharesCount || 0)} {t('shares')}
              </h5>
            </div>
          </div>
          <div className="col-6 col-md-4">
            <div
              className="h-100"
              style={{
                borderLeft: '3px solid #f4bd0e',
                paddingLeft: '12px',
              }}
            >
              <span className="overline-title text-soft">
                {t('PricePerShare')}
              </span>
              <h5 className="mb-0 mt-1">
                {formatCurrencyDecimals(operation?.sharePrice || 0)}
              </h5>
              <span className="text-soft fs-13px">
                {formatCurrencyDecimals(operation?.totalPrice || 0)}
              </span>
            </div>
          </div>
          <div className="col-6 col-md-4">
            <div
              className="h-100"
              style={{
                borderLeft: '3px solid #6576ff',
                paddingLeft: '12px',
              }}
            >
              <span className="overline-title text-soft">
                {t('TotalAmount')}
              </span>
              <h5 className="mb-0 mt-1 text-primary">
                {formatCurrencyDecimals(operation?.totalPrice || 0)}
              </h5>
              <span className="text-soft fs-13px">
                {formatCurrencyDecimals(operation?.sharePrice || 0)}
              </span>
            </div>
          </div>
          <div className="col-6 col-md-4">
            <div
              className="h-100"
              style={{
                borderLeft: '3px solid #8094ae',
                paddingLeft: '12px',
              }}
            >
              <span className="overline-title text-soft">
                {t('RequestDate')}
              </span>
              <h5 className="mb-0 mt-1">{formattedRequestDate}</h5>
              <span className="text-soft fs-13px">
                {operation?.requestDate
                  ? format(new Date(operation.requestDate), "HH:mm'h'", {
                      locale: es,
                    })
                  : ''}
              </span>
            </div>
          </div>
          <div className="col-6 col-md-4">
            <div
              className="h-100"
              style={{
                borderLeft: '3px solid #e85347',
                paddingLeft: '12px',
              }}
            >
              <span className="overline-title text-soft">
                {t('CompletionDate')}
              </span>
              <h5 className="mb-0 mt-1">
                {operation?.completionDate
                  ? formatDate(operation.completionDate)
                  : t('Pending')}
              </h5>
              <span className="text-soft fs-13px">{completionTimeLabel}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingOfferOperationDetailSummaryCard;
